import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  Activity,
  BadgeCheck,
  Building2,
  CheckCircle2,
  ChevronRight,
  Copy,
  CreditCard,
  Eye,
  EyeOff,
  ExternalLink,
  Fingerprint,
  IdCard,
  KeyRound,
  LockKeyhole,
  Mail,
  Plus,
  RefreshCw,
  ScanLine,
  Search,
  ShieldCheck,
  Smartphone,
  UserRound,
  Users,
  WalletCards,
  XCircle,
} from 'lucide-react';
import { api, dateOnly, dateTime } from '../lib/api';
import { EmptyState, Status } from '../components/UI';

const req = (method, body = {}) => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

const blankForm = {
  staff_id: '',
  legal_name: '',
  photo_url: '',
  internal_title: '',
  company_number: '',
  relationship_source: 'companies_house',
  companies_house_officer_key: '',
  companies_house_personal_code: '',
  expires_at: '',
  identity_check_method: 'staff_record',
  identity_check_confirmed: false,
};

const titleCase = value => String(value || '').replaceAll('_', ' ').replace(/\b\w/g, character => character.toUpperCase());
const initials = value => String(value || '?').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase() || '?';
const compactReference = value => {
  const text = String(value || '').trim();
  if (!text) return 'Instance recorded';
  if (text.length <= 18) return text;
  return `${text.slice(0, 8)}…${text.slice(-6)}`;
};

export default function CompanyIDAdministration() {
  const navigate = useNavigate();
  const location = useLocation();
  const allowedTabs = new Set(['overview','holders','credentials','issuers','issue','verification','wallets','lifecycle','audit']);
  const requestedTab = new URLSearchParams(location.search).get('tab') || 'overview';
  const initialTab = allowedTabs.has(requestedTab) ? requestedTab : 'overview';
  const [tab, setTab] = useState(initialTab);
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(blankForm);
  const [company, setCompany] = useState(null);
  const [officers, setOfficers] = useState([]);
  const [issueStep, setIssueStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [revealedCode, setRevealedCode] = useState('');
  const [qr, setQr] = useState('');
  const [presentation, setPresentation] = useState(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [walletTest, setWalletTest] = useState(null);

  const credentials = data?.credentials || [];
  const selected = credentials.find(row => row.id === selectedId) || credentials[0] || null;
  const staffById = useMemo(() => new Map((data?.staff || []).map(row => [row.id, row])), [data?.staff]);

  const filteredCredentials = useMemo(() => {
    const q = query.trim().toLowerCase();
    return credentials.filter(row => {
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (!q) return true;
      return [
        row.legal_name,
        row.display_name,
        row.staff_email,
        row.staff_id_number,
        row.credential_number,
        row.company_name,
        row.company_number,
        row.internal_title,
        row.companies_house_role,
      ].some(value => String(value || '').toLowerCase().includes(q));
    });
  }, [credentials, query, statusFilter]);

  const metrics = useMemo(() => ({
    issued: credentials.length,
    active: credentials.filter(row => row.status === 'active').length,
    verified: credentials.filter(row => ['verified', 'employer_verified'].includes(row.relationship_status)).length,
    needsAttention: credentials.filter(row => row.status !== 'active' || !['verified', 'employer_verified'].includes(row.relationship_status)).length,
  }), [credentials]);

  const load = async (preserve = true) => {
    setLoading(true);
    try {
      const result = await api('/api/admin/company-id');
      setData(result);
      setForm(current => ({
        ...current,
        company_number: current.company_number || result?.defaults?.company_number || '',
      }));
      if (!preserve || !selectedId) setSelectedId(result?.credentials?.[0]?.id || '');
      setNotice('');
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, []);

  useEffect(() => {
    setRevealedCode('');
    setQr('');
    setPresentation(null);
    if (tab !== 'verification' || !selected?.id || selected.status !== 'active') return undefined;
    let cancelled = false;
    let timer = null;

    const issuePresentation = async () => {
      try {
        const next = await api(`/api/admin/company-id/${encodeURIComponent(selected.id)}/presentation`, req('POST'));
        if (cancelled) return;
        setPresentation(next);
        const image = await QRCode.toDataURL(next.verification_url, {
          width: 260,
          margin: 1,
          errorCorrectionLevel: 'M',
        });
        if (!cancelled) setQr(image);
      } catch {
        if (!cancelled) {
          setPresentation(null);
          setQr('');
        }
      }
    };

    issuePresentation();
    timer = window.setInterval(issuePresentation, 60000);
    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [tab, selected?.id, selected?.status]);

  const chooseStaff = value => {
    const staff = staffById.get(value);
    setForm(current => ({
      ...current,
      staff_id: value,
      legal_name: current.staff_id === value ? current.legal_name : (staff?.display_name || ''),
      internal_title: current.staff_id === value ? current.internal_title : (staff?.job_title || ''),
    }));
  };

  const lookup = async () => {
    const number = form.company_number.trim();
    if (!number) {
      setNotice('Error: Enter the Companies House company number.');
      return;
    }
    setBusy(true);
    setNotice('');
    try {
      const result = await api(`/api/admin/company-id/companies-house/company/${encodeURIComponent(number)}`);
      setCompany(result.company || null);
      setOfficers(result.officers || []);
      setForm(current => ({ ...current, companies_house_officer_key: '' }));
      setNotice(`${result.company?.company_name || 'Company'} loaded from Companies House.`);
    } catch (error) {
      setCompany(null);
      setOfficers([]);
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const issue = async () => {
    setBusy(true);
    setNotice('');
    try {
      const result = await api('/api/admin/company-id', req('POST', form));
      setForm(current => ({ ...blankForm, company_number: current.company_number }));
      setCompany(null);
      setOfficers([]);
      setIssueStep(1);
      const issuedId = result.credential?.id || '';
      setNotice(`Staff ID ${result.staff_id_number || result.credential?.staff_id_number || ''} issued with live Company ID credential.`);
      await load(true);
      if (issuedId) navigate(`/company-id/record/${encodeURIComponent(issuedId)}`);
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (row, status) => {
    let reason = '';
    if (status === 'revoked') {
      reason = window.prompt('Reason for revoking this Company ID:') || '';
      if (!reason.trim()) return;
    }
    if (status === 'suspended' && !window.confirm('Suspend this Company ID? Its live verification will immediately stop reporting it as valid.')) return;
    setBusy(true);
    try {
      await api(`/api/admin/company-id/${encodeURIComponent(row.id)}/status`, req('POST', { status, reason }));
      setNotice(`Company ID ${status}.`);
      await load(true);
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const refreshRelationship = async row => {
    setBusy(true);
    try {
      await api(`/api/admin/company-id/${encodeURIComponent(row.id)}/refresh-companies-house`, req('POST'));
      setNotice('Companies House relationship refreshed.');
      await load(true);
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const reveal = async row => {
    if (revealedCode) {
      setRevealedCode('');
      return;
    }
    setBusy(true);
    try {
      const result = await api(`/api/admin/company-id/${encodeURIComponent(row.id)}/reveal-personal-code`, req('POST'));
      setRevealedCode(result.personal_code || '');
      window.setTimeout(() => setRevealedCode(''), 60000);
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const copy = async value => {
    try {
      await navigator.clipboard.writeText(value);
      setNotice('Copied to clipboard.');
    } catch {
      setNotice('Error: Your browser did not allow copying.');
    }
  };

  const rotatePublicLink = async row => {
    if (!window.confirm('Rotate this Company ID permanent verification link? Existing permanent links will stop working immediately.')) return;
    setBusy(true);
    setNotice('');
    try {
      const result = await api(`/api/admin/company-id/${encodeURIComponent(row.id)}/rotate-public-link`, req('POST'));
      setNotice('Permanent verification link rotated.');
      if (result?.credential?.id) setSelectedId(result.credential.id);
      await load(true);
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const issueAppleWallet = async (row, deliveryEmail = '') => {
    if (!row?.id) return;
    setBusy(true);
    setNotice('');
    try {
      const result = await api(`/api/admin/company-id/${encodeURIComponent(row.id)}/wallet/apple`, req('POST', {
        delivery_email: String(deliveryEmail || '').trim(),
      }));
      const delivery = result?.email_sent
        ? ` Wallet email sent to ${result.delivery_email}.`
        : result?.delivery_email
          ? ' Pass Studio did not report an email delivery.'
          : ' No staff email address is available, so use the Apple Wallet link directly.';
      const fieldNote = Array.isArray(result?.mapped_fields) && result.mapped_fields.length
        ? ` Synced fields: ${result.mapped_fields.join(', ')}.`
        : '';
      const baseMessage = result?.resent_existing
        ? 'Existing Apple Wallet pass updated and delivery email requested again.'
        : result?.already_issued
          ? 'Apple Wallet pass already exists and is ready to use.'
          : 'Apple Wallet pass issued through Pass Studio.';
      setNotice(baseMessage + fieldNote + delivery);
      await load(true);
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const testPassStudio = async () => {
    setBusy(true);
    setWalletTest(null);
    setNotice('');
    try {
      const result = await api('/api/admin/company-id/wallet/pass-studio/test', req('POST'));
      setWalletTest(result);
      setNotice(`Pass Studio connected${result?.pass?.name ? `: ${result.pass.name}` : ''}.`);
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const selectTab = next => {
    setTab(next);
    navigate(`/company-id?tab=${encodeURIComponent(next)}`, { replace: true });
  };

  const openCredential = row => {
    if (!row?.id) return;
    navigate(`/company-id/record/${encodeURIComponent(row.id)}`);
  };

  const openHolder = row => {
    if (!row?.id) return;
    navigate(`/company-id/holder/${encodeURIComponent(row.id)}`);
  };

  const startIssue = () => {
    setIssueStep(1);
    selectTab('issue');
  };

  const tabs = [
    ['overview', 'Control Centre', ShieldCheck, 'Authority health and trust services'],
    ['holders', 'Holder Registry', Users, 'Permanent staff identities'],
    ['credentials', 'Credential Registry', IdCard, 'Issued Company ID credentials'],
    ['issuers', 'Issuer Registry', Building2, 'Authorised legal entities'],
    ['issue', 'Issuance', Plus, 'Create a new credential'],
    ['verification', 'Verification', ScanLine, 'Live presentation and verifier'],
    ['wallets', 'Wallet Delivery', WalletCards, 'Apple Wallet and delivery'],
    ['lifecycle', 'Lifecycle', Activity, 'Suspend, revoke and replace'],
    ['audit', 'Audit & Policy', LockKeyhole, 'Events, policy and assurance'],
  ];

  return <section className="cid-platform">
    <header className="cid-platform-header">
      <div className="cid-brand-block">
        <div className="cid-brand-icon"><Fingerprint size={27}/></div>
        <div>
          <span className="cid-kicker">SOUSA MURRAY CREDENTIAL AUTHORITY</span>
          <h1 className="govuk-heading-xl">Company ID Infrastructure</h1>
          <p className="govuk-body">Central issuer infrastructure for permanent holder identities, credential issuance, Wallet delivery, live verification, lifecycle control and audit.</p>
        </div>
      </div>
      <div className="cid-header-actions">
        <button className="cid-button cid-button-ghost" onClick={() => load(true)} disabled={busy}><RefreshCw size={16}/>Refresh</button>
        <button className="cid-button cid-button-primary" onClick={startIssue}><Plus size={16}/>Issue Company ID</button>
      </div>
    </header>

    <div className="cid-authority-strip">
      <span><BadgeCheck size={15}/><strong>Credential authority online</strong></span>
      <span><Fingerprint size={15}/>SMCID-1</span>
      <span><ScanLine size={15}/>Live verifier</span>
      <span><WalletCards size={15}/>{data?.wallet?.apple?.configured ? 'Wallet issuer connected' : 'Wallet setup required'}</span>
    </div>

    {notice && <div className={`cid-notice ${notice.startsWith('Error:') ? 'error' : ''}`}>{notice}</div>}

    <div className="cid-infra-shell">
      <nav className="cid-infra-nav" aria-label="Company ID infrastructure">
        <div className="cid-infra-nav-head"><span>ISSUER CONSOLE</span><strong>Trust Services</strong></div>
        {tabs.map(([id, label, Icon, description]) =>
          <button type="button" key={id} aria-pressed={tab===id} className={tab === id ? 'active' : ''} onClick={() => selectTab(id)}>
            <Icon size={17}/>
            <span><strong>{label}</strong><small>{description}</small></span>
            <ChevronRight size={14}/>
          </button>
        )}
      </nav>

      <div className="cid-infra-content">
        {loading && !data ? <div className="cid-loading"><div className="cid-spinner"/><strong>Loading Company ID infrastructure</strong><span>Reading registries, credentials and trust services…</span></div> : <>
          {tab === 'overview' && <Overview metrics={metrics} data={data} credentials={credentials} startIssue={startIssue} openCredential={openCredential}/>}
          {tab === 'holders' && <HolderRegistry holders={data?.holders || []} openHolder={openHolder} startIssue={startIssue}/>}
          {tab === 'credentials' && <Credentials
            credentials={filteredCredentials}
            allCredentials={credentials}
            query={query}
            setQuery={setQuery}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            openCredential={openCredential}
            startIssue={startIssue}
          />}
          {tab === 'issuers' && <IssuerRegistry issuers={data?.issuers || []} credentials={credentials} openCredential={openCredential}/>}
          {tab === 'issue' && <IssueCredential
            data={data}
            form={form}
            setForm={setForm}
            issueStep={issueStep}
            setIssueStep={setIssueStep}
            chooseStaff={chooseStaff}
            company={company}
            officers={officers}
            lookup={lookup}
            issue={issue}
            busy={busy}
          />}
          {tab === 'verification' && <VerificationHub selected={selected} qr={qr} presentation={presentation} setSelectedId={setSelectedId} credentials={credentials}/>}
          {tab === 'wallets' && <WalletSecurity data={data} busy={busy} onTest={testPassStudio} testResult={walletTest}/>}
          {tab === 'lifecycle' && <LifecycleConsole lifecycle={data?.lifecycle} credentials={credentials} openCredential={openCredential}/>}
          {tab === 'audit' && <AuditPolicy data={data} openCredential={openCredential}/>}
        </>}
      </div>
    </div>
  </section>;
}

function Overview({ metrics, data, credentials, startIssue, openCredential }) {
  const ready = Boolean(data?.security?.personal_code_encryption_configured);
  return <div className="cid-overview">
    <section className="cid-hero">
      <div className="cid-hero-copy">
        <span className="cid-trust-pill"><BadgeCheck size={15}/> CREDENTIAL AUTHORITY CONTROL CENTRE</span>
        <h2 className="govuk-heading-m">One authority for holder identity, credentials and live trust.</h2>
        <p className="govuk-body">Operate the complete Sousa Murray Company ID trust chain: permanent holder identities, controlled credential issuance, Wallet delivery, live verification, lifecycle enforcement and a retained audit ledger.</p>
        <div className="cid-hero-actions">
          <button className="cid-button cid-button-light" onClick={startIssue}><Plus size={16}/>Issue credential</button>
          <span><ShieldCheck size={16}/> SMCID-1 issuer authority</span>
        </div>
      </div>
      <div className="cid-hero-visual" aria-hidden="true">
        <div className="cid-mini-phone">
          <div className="cid-mini-status">SOUSA MURRAY <BadgeCheck size={17}/></div>
          <div className="cid-mini-avatar"><UserRound size={34}/></div>
          <strong>COMPANY ID</strong>
          <span>Verified company credential</span>
          <div className="cid-mini-qr"><ScanLine size={42}/></div>
        </div>
      </div>
    </section>

    <section className="cid-stat-grid">
      <Stat icon={Users} label="Registered holders" value={data?.holders?.length || 0} detail="Permanent Staff IDs"/>
      <Stat icon={IdCard} label="Credential records" value={metrics.issued} detail="Full lifecycle registry"/>
      <Stat icon={Building2} label="Issuing entities" value={data?.issuers?.length || 0} detail="Issuer authority registry"/>
      <Stat icon={CheckCircle2} label="Active credentials" value={metrics.active} detail="Valid for live verification"/>
    </section>

    <section className="cid-panel cid-authority-topology-panel">
      <div className="cid-panel-heading">
        <div><span className="cid-panel-kicker">AUTHORITY TOPOLOGY</span><h3 className="govuk-heading-s">Company ID trust infrastructure</h3><p className="govuk-body">Each layer has its own record and lifecycle. No single Wallet pass or card is the source of truth.</p></div>
      </div>
      <div className="cid-authority-topology">
        <TopologyNode icon={Users} label="Holder Registry" body="Permanent Staff ID"/>
        <ChevronRight size={16}/>
        <TopologyNode icon={Plus} label="Issuance Authority" body="Identity + relationship checks"/>
        <ChevronRight size={16}/>
        <TopologyNode icon={IdCard} label="Credential Registry" body="SMCID-1 lifecycle record"/>
        <ChevronRight size={16}/>
        <TopologyNode icon={WalletCards} label="Delivery & Verification" body="Wallet + live verifier"/>
        <ChevronRight size={16}/>
        <TopologyNode icon={Activity} label="Lifecycle & Audit" body="Suspend, revoke, retain"/>
      </div>
    </section>

    <div className="cid-overview-grid">
      <section className="cid-panel">
        <div className="cid-panel-heading">
          <div><span className="cid-panel-kicker">TRUST CHAIN</span><h3 className="govuk-heading-s">How a Company ID becomes trusted</h3></div>
        </div>
        <div className="cid-assurance-list">
          <AssuranceStep number="01" title="Permanent Staff ID" body="The holder receives one Sousa Murray Staff ID number that remains theirs across credential reissues." state="ready"/>
          <AssuranceStep number="02" title="Holder identity assurance" body="Issuance requires an authorised staff member to record how the holder details were checked." state="ready"/>
          <AssuranceStep number="03" title="Company relationship" body="Authority is backed by either a live Companies House appointment or the current employer staff record." state="ready"/>
          <AssuranceStep number="04" title="Credential security" body="Sensitive Companies House personal codes are encrypted and restricted." state={ready ? 'ready' : 'attention'}/>
          <AssuranceStep number="05" title="Live presentation" body="A rotating QR presentation proves the credential is active at the time it is shown." state="ready"/>
          <AssuranceStep number="06" title="Wallet" body="Apple Wallet and Google Wallet can be enabled once issuer credentials are configured." state="planned"/>
        </div>
      </section>

      <section className="cid-panel">
        <div className="cid-panel-heading">
          <div><span className="cid-panel-kicker">PLATFORM HEALTH</span><h3 className="govuk-heading-s">Credential service readiness</h3></div>
        </div>
        <div className="cid-health-list">
          <HealthRow label="Companies House connection" value="Connected" ok/>
          <HealthRow label="Encrypted personal-code vault" value={ready ? 'Configured' : 'Needs secret'} ok={ready}/>
          <HealthRow label="Rotating QR verification" value="Active" ok/>
          <HealthRow label="Apple Wallet issuer" value={data?.wallet?.apple?.configured ? 'Issuer detected' : 'Not configured'} ok={Boolean(data?.wallet?.apple?.configured)} neutral={!data?.wallet?.apple?.configured}/>
          <HealthRow label="Google Wallet issuer" value={data?.wallet?.google?.configured ? 'Issuer detected' : 'Not configured'} ok={Boolean(data?.wallet?.google?.configured)} neutral={!data?.wallet?.google?.configured}/>
        </div>
      </section>
    </div>

    <section className="cid-panel">
      <div className="cid-panel-heading">
        <div><span className="cid-panel-kicker">RECENT REGISTRY ACTIVITY</span><h3 className="govuk-heading-s">Credential records</h3></div>
        {credentials.length > 0 && <button className="cid-text-button" onClick={() => openCredential(credentials[0])}>Open credential directory <ChevronRight size={15}/></button>}
      </div>
      {credentials.length ? <div className="cid-recent-grid">{credentials.slice(0, 3).map(row => <CredentialTile key={row.id} row={row} onClick={() => openCredential(row)}/>)}</div> :
        <div className="cid-empty-platform"><div className="cid-empty-icon"><IdCard size={26}/></div><strong>No Company IDs issued yet</strong><p className="govuk-body">Create the first formal company-issued credential and test the full holder and verifier journey.</p><button className="cid-button cid-button-primary" onClick={startIssue}><Plus size={15}/>Issue Company ID</button></div>}
    </section>
  </div>;
}

function Stat({ icon: Icon, label, value, detail }) {
  return <div className="cid-stat-card"><div className="cid-stat-icon"><Icon size={19}/></div><div><span>{label}</span><strong>{value}</strong><small>{detail}</small></div></div>;
}

function TopologyNode({ icon: Icon, label, body }) {
  return <div className="cid-topology-node"><span><Icon size={18}/></span><strong>{label}</strong><small>{body}</small></div>;
}

function AssuranceStep({ number, title, body, state }) {
  return <div className="cid-assurance-step">
    <span className="cid-assurance-number">{number}</span>
    <div><strong>{title}</strong><p className="govuk-body">{body}</p></div>
    <span className={`cid-assurance-state ${state}`}>{state === 'ready' ? <CheckCircle2 size={17}/> : state === 'attention' ? <XCircle size={17}/> : <CreditCard size={17}/>}</span>
  </div>;
}

function HealthRow({ label, value, ok, neutral }) {
  return <div className="cid-health-row"><span>{label}</span><strong className={neutral ? 'neutral' : ok ? 'ok' : 'bad'}>{ok ? <CheckCircle2 size={15}/> : neutral ? <CreditCard size={15}/> : <XCircle size={15}/>} {value}</strong></div>;
}

function CredentialTile({ row, onClick }) {
  return <button className="cid-credential-tile" onClick={onClick}>
    <div className="cid-tile-top"><span className="cid-tile-avatar">{initials(row.legal_name)}</span><Status value={row.status}/></div>
    <strong>{row.legal_name}</strong>
    <span>{row.internal_title || titleCase(row.companies_house_role)}</span>
    <div className="cid-tile-company"><Building2 size={14}/>{row.company_name}</div>
    <small>Staff ID {row.staff_id_number || 'Pending'} · {row.credential_number}</small>
  </button>;
}

function HolderRegistry({ holders, openHolder, startIssue }) {
  const [holderQuery, setHolderQuery] = useState('');
  const filtered = holders.filter(holder => {
    const q = holderQuery.trim().toLowerCase();
    if (!q) return true;
    return [
      holder.display_name,
      holder.legal_name,
      holder.email,
      holder.job_title,
      holder.staff_id_number,
      ...(holder.companies || []).flatMap(company => [company.company_name, company.company_number]),
    ].some(value => String(value || '').toLowerCase().includes(q));
  });

  return <section className="cid-panel cid-registry-panel">
    <div className="cid-panel-heading">
      <div>
        <span className="cid-panel-kicker">PERMANENT IDENTITY REGISTRY</span>
        <h3 className="govuk-heading-s">Holder Registry</h3>
        <p className="govuk-body">One permanent Staff ID per person. Credentials can be reissued or revoked without replacing the holder identity.</p>
      </div>
      <button className="cid-button cid-button-primary cid-button-small" onClick={startIssue}><Plus size={15}/>Issue credential</button>
    </div>

    <div className="cid-registry-summary">
      <RegistryCount label="Registered holders" value={holders.length}/>
      <RegistryCount label="With active credential" value={holders.filter(holder => holder.active_credentials > 0).length}/>
      <RegistryCount label="With Wallet credential" value={holders.filter(holder => holder.wallet_credentials > 0).length}/>
      <RegistryCount label="Historical/revoked" value={holders.filter(holder => holder.revoked_credentials > 0).length}/>
    </div>

    <label className="cid-search cid-registry-search govuk-label"><Search size={16}/><input className="govuk-input" value={holderQuery} onChange={event => setHolderQuery(event.target.value)} placeholder="Search holder, Staff ID, email or issuer"/></label>

    {filtered.length ? <div className="cid-holder-registry">{filtered.map(holder =>
      <button key={holder.id} className="cid-holder-registry-row" onClick={() => openHolder(holder)}>
        <span className="cid-directory-avatar">{holder.photo_url ? <img src={holder.photo_url} alt=""/> : initials(holder.display_name)}</span>
        <span className="cid-holder-registry-identity">
          <strong>{holder.display_name}</strong>
          <small>{holder.job_title || 'Staff member'} · {holder.email || 'No email on record'}</small>
          <span>{(holder.companies || []).map(company => company.company_name).join(' · ') || 'No issuer recorded'}</span>
        </span>
        <span className="cid-holder-registry-staff-id"><small>PERMANENT STAFF ID</small><strong>{holder.staff_id_number || 'Pending'}</strong></span>
        <span className="cid-holder-registry-count"><strong>{holder.credential_count}</strong><small>credential{holder.credential_count === 1 ? '' : 's'}</small></span>
        <span className={`cid-holder-registry-state ${holder.active_credentials ? 'active' : holder.suspended_credentials ? 'suspended' : 'historical'}`}>
          {holder.active_credentials ? 'ACTIVE' : holder.suspended_credentials ? 'SUSPENDED' : 'HISTORICAL'}
        </span>
        <ChevronRight size={18}/>
      </button>
    )}</div> : <EmptyState title="No matching holders" body="No permanent Company ID holder matches this search."/>}
  </section>;
}

function IssuerRegistry({ issuers, credentials, openCredential }) {
  return <div className="cid-infra-section">
    <section className="cid-panel">
      <div className="cid-panel-heading">
        <div>
          <span className="cid-panel-kicker">ISSUER AUTHORITY REGISTRY</span>
          <h3 className="govuk-heading-s">Issuing legal entities</h3>
          <p className="govuk-body">Legal entities currently represented in the Company ID credential registry.</p>
        </div>
      </div>
      {issuers.length ? <div className="cid-issuer-grid">{issuers.map(issuer =>
        <article key={issuer.company_number || issuer.company_name} className="cid-issuer-card">
          <div className="cid-issuer-card-head">
            <span className="cid-issuer-icon"><Building2 size={20}/></span>
            <span className={`cid-issuer-company-state ${String(issuer.company_status || '').toLowerCase() === 'active' ? 'active' : 'attention'}`}>{titleCase(issuer.company_status || 'Unknown')}</span>
          </div>
          <h4>{issuer.company_name}</h4>
          <code>{issuer.company_number || 'No company number'}</code>
          <div className="cid-issuer-metrics">
            <div><strong>{issuer.holder_count}</strong><span>Holders</span></div>
            <div><strong>{issuer.active_credentials}</strong><span>Active</span></div>
            <div><strong>{issuer.credential_count}</strong><span>Total</span></div>
            <div><strong>{issuer.wallet_credentials}</strong><span>Wallet</span></div>
          </div>
          <div className="cid-issuer-actions">
            {credentials.find(row => row.company_number === issuer.company_number) &&
              <button className="cid-text-button" onClick={() => openCredential(credentials.find(row => row.company_number === issuer.company_number))}>Open latest credential <ChevronRight size={14}/></button>}
          </div>
        </article>
      )}</div> : <EmptyState title="No issuing entities" body="Issuers appear here as soon as the first Company ID credential is issued."/>}
    </section>

    <section className="cid-panel cid-issuer-authority-note">
      <div className="cid-panel-heading compact"><div><span className="cid-panel-kicker">AUTHORITY MODEL</span><h3 className="govuk-heading-s">What an issuer controls</h3></div></div>
      <div className="cid-authority-capability-grid">
        <Capability icon={Users} title="Holder identities" body="Assign and retain permanent Staff ID records."/>
        <Capability icon={IdCard} title="Credential issuance" body="Issue SMCID-1 credentials against a current company relationship."/>
        <Capability icon={ScanLine} title="Verification" body="Publish a live verifier that checks current status at scan time."/>
        <Capability icon={Activity} title="Lifecycle" body="Suspend, revoke and replace credentials without deleting history."/>
      </div>
    </section>
  </div>;
}

function LifecycleConsole({ lifecycle, credentials, openCredential }) {
  const attention = credentials.filter(row => row.status !== 'active' || !['verified','employer_verified'].includes(row.relationship_status));
  const revoked = credentials.filter(row => row.status === 'revoked');
  return <div className="cid-infra-section">
    <section className="cid-panel">
      <div className="cid-panel-heading">
        <div><span className="cid-panel-kicker">CREDENTIAL LIFECYCLE</span><h3 className="govuk-heading-s">Status control and revocation registry</h3><p className="govuk-body">Credential lifecycle changes are retained as auditable records. Revocation is final; replacement creates a new credential.</p></div>
      </div>
      <div className="cid-lifecycle-metrics">
        <LifecycleMetric label="Active" value={lifecycle?.active || 0} state="active"/>
        <LifecycleMetric label="Suspended" value={lifecycle?.suspended || 0} state="suspended"/>
        <LifecycleMetric label="Revoked" value={lifecycle?.revoked || 0} state="revoked"/>
        <LifecycleMetric label="Wallet issued" value={lifecycle?.wallet_issued || 0} state="wallet"/>
        <LifecycleMetric label="Relationship attention" value={lifecycle?.relationship_attention || 0} state="attention"/>
      </div>
    </section>

    <section className="cid-panel">
      <div className="cid-panel-heading compact"><div><span className="cid-panel-kicker">ACTION QUEUE</span><h3 className="govuk-heading-s">Credentials requiring attention</h3></div></div>
      {attention.length ? <div className="cid-lifecycle-list">{attention.map(row =>
        <button key={row.id} onClick={() => openCredential(row)}>
          <span className="cid-lifecycle-symbol"><Activity size={16}/></span>
          <span><strong>{row.legal_name}</strong><small>{row.credential_number} · {row.company_name}</small></span>
          <span>{row.status !== 'active' ? titleCase(row.status) : titleCase(row.relationship_status)}</span>
          <ChevronRight size={16}/>
        </button>
      )}</div> : <div className="cid-inline-empty"><CheckCircle2 size={19}/><span>No credentials currently require lifecycle attention.</span></div>}
    </section>

    <section className="cid-panel">
      <div className="cid-panel-heading compact"><div><span className="cid-panel-kicker">REVOCATION REGISTRY</span><h3 className="govuk-heading-s">Revoked credential history</h3></div></div>
      {revoked.length ? <div className="cid-revocation-table">{revoked.map(row =>
        <button key={row.id} onClick={() => openCredential(row)}>
          <code>{row.credential_number}</code>
          <span><strong>{row.legal_name}</strong><small>{row.company_name}</small></span>
          <span>{row.revocation_reason || 'Revoked by issuer'}</span>
          <span>{row.revoked_at ? dateTime(row.revoked_at) : 'Date unavailable'}</span>
          <ChevronRight size={15}/>
        </button>
      )}</div> : <div className="cid-inline-empty"><ShieldCheck size={19}/><span>No revoked credentials are recorded.</span></div>}
    </section>
  </div>;
}

function AuditPolicy({ data, openCredential }) {
  const policy = data?.policy || {};
  const events = data?.recent_events || [];
  return <div className="cid-infra-section">
    <section className="cid-panel">
      <div className="cid-panel-heading">
        <div><span className="cid-panel-kicker">CREDENTIAL POLICY</span><h3 className="govuk-heading-s">SMCID-1 trust policy</h3><p className="govuk-body">Operational rules enforced by the Company ID authority.</p></div>
      </div>
      <div className="cid-policy-grid">
        <PolicyRule label="Credential standard" value={policy.credential_standard || 'SMCID-1'} good/>
        <PolicyRule label="Permanent holder identity" value={policy.permanent_staff_identity ? 'Required' : 'Not enforced'} good={policy.permanent_staff_identity}/>
        <PolicyRule label="Identity assurance" value={policy.identity_assurance_required ? 'Required before issue' : 'Optional'} good={policy.identity_assurance_required}/>
        <PolicyRule label="Live verification" value={policy.live_verification_required ? 'Required' : 'Optional'} good={policy.live_verification_required}/>
        <PolicyRule label="QR presentation lifetime" value={policy.presentation_ttl_seconds ? `${policy.presentation_ttl_seconds} seconds` : '—'}/>
        <PolicyRule label="Revocation" value={policy.revocation_reversible === false ? 'Irreversible' : 'Reversible'} good={policy.revocation_reversible === false}/>
        <PolicyRule label="Government-issued ID" value={policy.government_issued ? 'Yes' : 'No — employer-issued credential'} good={!policy.government_issued}/>
        <PolicyRule label="Restricted personal codes" value={data?.security?.personal_code_publicly_shared ? 'Public' : 'Never public'} good={!data?.security?.personal_code_publicly_shared}/>
      </div>
    </section>

    <section className="cid-panel">
      <div className="cid-panel-heading compact"><div><span className="cid-panel-kicker">AUTHORITY AUDIT LOG</span><h3 className="govuk-heading-s">Recent infrastructure activity</h3></div></div>
      {events.length ? <div className="cid-audit-ledger">{events.map(event =>
        <button key={event.id} onClick={() => event.company_id && openCredential({ id: event.company_id })}>
          <span className="cid-audit-event-icon"><Activity size={15}/></span>
          <span className="cid-audit-event-main"><strong>{event.summary || titleCase(event.event_type)}</strong><small>{event.legal_name || 'System event'}{event.credential_number ? ` · ${event.credential_number}` : ''}</small></span>
          <span className="cid-audit-event-type">{titleCase(event.event_type)}</span>
          <time>{dateTime(event.occurred_at)}</time>
          <ChevronRight size={15}/>
        </button>
      )}</div> : <div className="cid-inline-empty"><Activity size={19}/><span>No Company ID audit events are recorded.</span></div>}
    </section>
  </div>;
}

function RegistryCount({ label, value }) {
  return <div><strong>{value}</strong><span>{label}</span></div>;
}

function Capability({ icon: Icon, title, body }) {
  return <div><span><Icon size={17}/></span><div><strong>{title}</strong><small>{body}</small></div></div>;
}

function LifecycleMetric({ label, value, state }) {
  return <div className={`cid-lifecycle-metric ${state}`}><strong>{value}</strong><span>{label}</span></div>;
}

function PolicyRule({ label, value, good }) {
  return <div className="cid-policy-rule"><span>{label}</span><strong className={good ? 'good' : ''}>{value}</strong></div>;
}

function Credentials({
  credentials,
  allCredentials,
  query,
  setQuery,
  statusFilter,
  setStatusFilter,
  openCredential,
  startIssue,
}) {
  return <section className="cid-panel cid-directory cid-directory-full">
    <div className="cid-panel-heading">
      <div><span className="cid-panel-kicker">CREDENTIAL REGISTRY</span><h3 className="govuk-heading-s">Issued Company ID credentials</h3><p className="govuk-body">Every credential is an immutable lifecycle record linked to a permanent holder identity.</p></div>
      <button className="cid-button cid-button-primary cid-button-small" onClick={startIssue}><Plus size={15}/>Issue ID</button>
    </div>

    <div className="cid-directory-toolbar">
      <label className="cid-search govuk-label"><Search size={16}/><input className="govuk-input" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search holder, Staff ID, company or credential"/></label>
      <select className="govuk-select" value={statusFilter} onChange={event => setStatusFilter(event.target.value)} aria-label="Filter Company IDs by status">
        <option value="all">All statuses</option>
        <option value="active">Active</option>
        <option value="suspended">Suspended</option>
        <option value="revoked">Revoked</option>
      </select>
    </div>

    {credentials.length ? <div className="cid-directory-list cid-directory-record-list">{credentials.map(row =>
      <button key={row.id} className="cid-directory-row cid-directory-record-row" onClick={() => openCredential(row)}>
        <span className="cid-directory-avatar">{row.photo_url ? <img src={row.photo_url} alt=""/> : initials(row.legal_name)}</span>
        <span className="cid-directory-main">
          <strong>{row.legal_name}</strong>
          <small>{row.internal_title || titleCase(row.companies_house_role) || 'Staff member'} · {row.company_name}</small>
          <span className="cid-directory-mini-meta">
            <span>Staff ID <b>{row.staff_id_number || 'Pending'}</b></span>
            <span>{row.staff_email || 'No email on record'}</span>
            <span>{row.wallet?.issued ? 'Apple Wallet issued' : 'Wallet not issued'}</span>
          </span>
        </span>
        <span className="cid-directory-ref">{row.credential_number}</span>
        <Status value={row.status}/>
        <ChevronRight size={18}/>
      </button>
    )}</div> :
      <EmptyState title={allCredentials.length ? 'No matching Company IDs' : 'No Company IDs yet'} body={allCredentials.length ? 'Change your search or status filter.' : 'Issue the first credential to begin your Company ID directory.'}/>}
  </section>;
}

export function CredentialWorkspace({ row, detail, qr, presentation, revealedCode, busy, reveal, copy, rotatePublicLink, refreshRelationship, changeStatus, wallet, issueAppleWallet }) {
  const [walletDeliveryEmail, setWalletDeliveryEmail] = useState(row.staff_email || '');
  useEffect(() => {
    setWalletDeliveryEmail(row.staff_email || '');
  }, [row.id, row.staff_email]);

  const personalCode = revealedCode || row.personal_code_masked || 'Not stored';
  const events = detail?.events || [];
  const relationshipCurrent = ['verified', 'employer_verified'].includes(row.relationship_status);

  return <>
    <section className="cid-panel cid-credential-summary">
      <div className="cid-panel-heading compact">
        <div><span className="cid-panel-kicker">FORMAL COMPANY CREDENTIAL</span><h3 className="govuk-heading-s">{row.legal_name}</h3></div>
        <Status value={row.status}/>
      </div>
      <div className="cid-device-frame">
        <div className="cid-device-speaker"/>
        <div className={`cid-id-card status-${row.status}`}>
          <div className="cid-card-head">
            <div className="cid-card-brand"><Fingerprint size={20}/><div><strong>SOUSA MURRAY</strong><span>COMPANY ID</span></div></div>
            <BadgeCheck size={26}/>
          </div>
          <div className="cid-card-person">
            {row.photo_url ? <img src={row.photo_url} alt=""/> : <div className="cid-card-avatar">{initials(row.legal_name)}</div>}
            <div><span>{row.credential_label || (row.relationship_source === 'companies_house' ? 'CORPORATE OFFICER IDENTIFICATION' : 'STAFF IDENTIFICATION')}</span><h2 className="govuk-heading-m">{row.legal_name}</h2><p className="govuk-body">{row.internal_title || titleCase(row.companies_house_role)}</p></div>
          </div>
          <div className="cid-card-company"><Building2 size={16}/><div><strong>{row.company_name}</strong><span>Company No. {row.company_number}</span></div></div>
          <div className="cid-card-details">
            <div><span>Staff ID</span><strong>{row.staff_id_number || '—'}</strong></div>
            <div><span>Authority</span><strong>{row.relationship_source === 'employer' ? 'Employer verified' : (titleCase(row.companies_house_role) || 'Companies House')}</strong></div>
          </div>
          <div className="cid-card-serial">{row.credential_format || 'SMCID-1'} · Credential serial · {row.credential_number}</div>
          <div className="cid-card-qr">
            {qr ? <img src={qr} alt="Rotating Company ID verification QR code"/> : <div className="cid-qr-skeleton"><ScanLine size={34}/></div>}
            <div><strong>Scan to verify</strong><span>{presentation?.expires_at ? 'Live rotating code' : row.status === 'active' ? 'Generating secure code…' : 'Credential is not active'}</span></div>
          </div>
          <div className="cid-card-foot"><span className={row.status === 'active' && relationshipCurrent ? 'verified' : 'blocked'}>{row.status === 'active' && relationshipCurrent ? <CheckCircle2 size={14}/> : <XCircle size={14}/>} {titleCase(row.status)}</span><span>Issued {dateOnly(row.issued_at)}</span></div>
        </div>
      </div>
      <p className="cid-card-privacy govuk-body"><LockKeyhole size={14}/> The QR never contains the Companies House personal code. It creates a short-lived live verification presentation.</p>
    </section>

    <section className="cid-panel">
      <div className="cid-panel-heading compact"><div><span className="cid-panel-kicker">ASSURANCE</span><h3 className="govuk-heading-s">Credential details</h3></div></div>
      <div className="cid-detail-grid">
        <Detail label="Credential type" value={row.credential_label || (row.relationship_source === 'companies_house' ? 'Corporate Officer Identification' : 'Staff Identification')}/><Detail label="Credential standard" value={row.credential_format || 'SMCID-1'} mono/><Detail label="Staff ID" value={row.staff_id_number} mono positive/>
        <Detail label="Holder" value={row.legal_name}/><Detail label="Holder photograph" value={row.holder_photo_present || row.photo_url ? 'On file' : 'Not on file'} positive={Boolean(row.holder_photo_present || row.photo_url)}/><Detail label="Identity assurance" value={row.identity_assurance === 'company_checked' ? 'Company checked' : 'Legacy — not recorded'} positive={row.identity_assurance === 'company_checked'}/><Detail label="Identity checked by" value={row.identity_checked_by_name || '—'}/>
        <Detail label="Company" value={row.company_name}/>
        <Detail label="Company number" value={row.company_number} mono/>
        <Detail label="Authority source" value={row.relationship_source === 'employer' ? 'Sousa Murray employer record' : 'Companies House'}/>
        <Detail label="Company role" value={row.relationship_source === 'employer' ? (row.internal_title || 'Staff member') : titleCase(row.companies_house_role)}/>
        <Detail label="Relationship" value={titleCase(row.relationship_status)} positive={relationshipCurrent}/>
        <Detail label="Credential serial" value={row.credential_number} mono/>
        <Detail label="Last company check" value={dateTime(row.companies_house_last_verified_at)}/>
        <Detail label="Issued by" value={row.issued_by_name || 'Authorised Company ID administrator'}/><Detail label="Expiry" value={row.expires_at ? dateOnly(row.expires_at) : 'Until revoked'}/>
        <div className="cid-detail-cell">
          <span>Companies House personal code</span>
          <div className="cid-sensitive-value"><code>{personalCode}</code>{row.personal_code_stored && <button onClick={() => reveal(row)} disabled={busy}>{revealedCode ? <EyeOff size={15}/> : <Eye size={15}/>}</button>}{revealedCode && <button onClick={() => copy(revealedCode)}><Copy size={15}/></button>}</div>
        </div>
      </div>
    </section>

    <section className="cid-panel">
      <div className="cid-panel-heading compact"><div><span className="cid-panel-kicker">CONTROLS</span><h3 className="govuk-heading-s">Manage credential</h3></div></div>
      <div className="cid-control-stack">
        <div><span><Building2 size={18}/><div><strong>Company authority</strong><small>Re-check the holder's current staff status, company status and Companies House appointment where applicable.</small></div></span><button className="cid-button cid-button-ghost cid-button-small" onClick={() => refreshRelationship(row)} disabled={busy}><RefreshCw size={14}/>Re-check</button></div>
        <div><span><ScanLine size={18}/><div><strong>Live verifier</strong><small>{presentation?.verification_url ? 'A short-lived verification presentation is ready.' : 'Verification is available for active credentials.'}</small></div></span>{presentation?.verification_url && <a className="cid-button cid-button-ghost cid-button-small" href={presentation.verification_url} target="_blank" rel="noreferrer">Open <ExternalLink size={13}/></a>}</div>
        <div><span><BadgeCheck size={18}/><div><strong>Permanent verification link</strong><small>Suitable for authorised staff directories and company email signatures. The destination performs a fresh live status check each time.</small></div></span>{row.persistent_verification_url && <span className="cid-control-actions"><button className="cid-button cid-button-ghost cid-button-small" onClick={() => copy(row.persistent_verification_url)}><Copy size={13}/>Copy</button><a className="cid-button cid-button-ghost cid-button-small" href={row.persistent_verification_url} target="_blank" rel="noreferrer">Open <ExternalLink size={13}/></a><button className="cid-button cid-button-ghost cid-button-small" onClick={() => rotatePublicLink(row)} disabled={busy}><RefreshCw size={13}/>Rotate</button></span>}</div>
        <div className="cid-wallet-control-row">
          <span className="cid-wallet-control-summary">
            <Smartphone size={18}/>
            <div>
              <strong>Apple Wallet · Pass Studio</strong>
              <small>
                {row.wallet?.issued
                  ? <>Wallet pass {titleCase(row.wallet.status || 'issued')} · <code title={row.wallet.instance_id || 'Instance recorded'}>{compactReference(row.wallet.instance_id)}</code></>
                  : wallet?.apple?.configured
                    ? 'Ready to issue this Company ID as a signed Apple Wallet pass.'
                    : 'Pass Studio API key and Company ID pass template must be configured.'}
              </small>
            </div>
          </span>
          <span className="cid-control-actions cid-wallet-actions">
            {row.wallet?.apple_wallet_url
              ? <>
                  <button className="cid-button cid-button-ghost cid-button-small" onClick={() => copy(row.wallet.apple_wallet_url)}><Copy size={13}/>Copy link</button>
                  <a className="cid-button cid-button-primary cid-button-small" href={row.wallet.apple_wallet_url} target="_blank" rel="noreferrer">Open Apple Wallet <ExternalLink size={13}/></a>
                </>
              : !wallet?.apple?.configured
                ? <span className="cid-coming">SETUP REQUIRED</span>
                : row.status !== 'active'
                  ? <span className="cid-coming">ACTIVE ID REQUIRED</span>
                  : null}
          </span>
          {wallet?.apple?.configured && row.status === 'active' && <div className="cid-wallet-delivery-editor">
            <label className="govuk-label">
              <span>Delivery email</span>
              <input className="govuk-input"
                type="email"
                value={walletDeliveryEmail}
                onChange={event => setWalletDeliveryEmail(event.target.value)}
                placeholder="name@example.com"
                autoComplete="off"
              />
            </label>
            <button
              className="cid-button cid-button-ghost cid-button-small"
              type="button"
              onClick={() => setWalletDeliveryEmail(row.staff_email || '')}
              disabled={busy || walletDeliveryEmail === (row.staff_email || '')}
            >
              <Mail size={13}/>Use holder email
            </button>
            <button
              className="cid-button cid-button-primary cid-button-small"
              type="button"
              onClick={() => issueAppleWallet(row, walletDeliveryEmail)}
              disabled={busy || !walletDeliveryEmail.trim()}
            >
              {row.wallet?.apple_wallet_url ? <RefreshCw size={13}/> : <WalletCards size={14}/>}
              {row.wallet?.apple_wallet_url ? 'Sync & send email' : 'Issue & send'}
            </button>
            <small>This changes only this Wallet delivery. It does not change the holder's staff email.</small>
          </div>}
        </div>
      </div>
      <div className="cid-danger-actions">
        {row.status === 'active' && <button className="cid-button cid-button-ghost" onClick={() => changeStatus(row, 'suspended')} disabled={busy}>Suspend</button>}
        {row.status === 'suspended' && <button className="cid-button cid-button-ghost" onClick={() => changeStatus(row, 'active')} disabled={busy}>Restore</button>}
        {row.status !== 'revoked' && <button className="cid-button cid-button-danger" onClick={() => changeStatus(row, 'revoked')} disabled={busy}>Revoke credential</button>}
      </div>
    </section>

    <section className="cid-panel">
      <div className="cid-panel-heading compact"><div><span className="cid-panel-kicker">AUDIT</span><h3 className="govuk-heading-s">Credential activity</h3></div></div>
      {events.length ? <div className="cid-event-list">{events.slice(0, 12).map(event => <div key={event.id} className="cid-event-row"><span className="cid-event-dot"/><div><strong>{event.summary || titleCase(event.event_type)}</strong><small>{dateTime(event.occurred_at)}{event.actor_name ? ` · ${event.actor_name}` : ''}</small></div></div>)}</div> :
        <div className="cid-inline-empty"><Activity size={19}/><span>No credential activity has been recorded yet.</span></div>}
    </section>
  </>;
}

function Detail({ label, value, mono, positive }) {
  return <div className="cid-detail-cell"><span>{label}</span><strong className={`${mono ? 'mono' : ''} ${positive ? 'positive' : ''}`}>{value || '—'}</strong></div>;
}

function IssueCredential({ data, form, setForm, issueStep, setIssueStep, chooseStaff, company, officers, lookup, issue, busy }) {
  const selectedStaff = (data?.staff || []).find(row => row.id === form.staff_id);
  const selectedOfficer = officers.find(row => row.officer_key === form.companies_house_officer_key);
  const canNext1 = Boolean(form.staff_id && form.legal_name.trim());
  const canNext2 = Boolean(company && (form.relationship_source === 'employer' || (selectedOfficer && !selectedOfficer.resigned_on)));
  const canIssue = Boolean(canNext2 && form.identity_check_confirmed && form.identity_check_method);

  return <div className="cid-issue-layout">
    <section className="cid-panel cid-issue-main">
      <div className="cid-panel-heading">
        <div><span className="cid-panel-kicker">FORMAL ISSUANCE WORKFLOW</span><h3 className="govuk-heading-s">Issue a Company ID</h3><p className="govuk-body">Create a formal company-issued identity credential with a verified holder and current company relationship.</p></div>
      </div>

      <div className="cid-stepper">
        <Step number="1" label="Holder" active={issueStep === 1} complete={issueStep > 1}/>
        <span/>
        <Step number="2" label="Company" active={issueStep === 2} complete={issueStep > 2}/>
        <span/>
        <Step number="3" label="Credential" active={issueStep === 3}/>
      </div>

      {issueStep === 1 && <div className="cid-issue-stage">
        <div className="cid-stage-intro"><div className="cid-stage-icon"><UserRound size={22}/></div><div><h4>Who will hold this Company ID?</h4><p className="govuk-body">Start with the authorised staff record. The holder's working details are copied into the credential and can be reviewed before issue.</p></div></div>
        <div className="cid-form-grid">
          <label className="wide govuk-label"><span>Holder</span><select className="govuk-select" required value={form.staff_id} onChange={event => chooseStaff(event.target.value)}><option value="">Select staff member</option>{(data?.staff || []).map(row => <option key={row.id} value={row.id}>{row.display_name} · {row.email}{row.staff_id_number ? ` · ${row.staff_id_number}` : ''}</option>)}</select></label>
          <label className="govuk-label"><span>Legal name on credential</span><input className="govuk-input" value={form.legal_name} onChange={event => setForm(value => ({ ...value, legal_name: event.target.value }))} placeholder="Full legal name"/></label>
          <label className="govuk-label"><span>Company title</span><input className="govuk-input" value={form.internal_title} onChange={event => setForm(value => ({ ...value, internal_title: event.target.value }))} placeholder="e.g. Chief Executive Officer"/></label>
          <label className="wide govuk-label"><span>Holder photograph <em>recommended for formal ID</em></span><input className="govuk-input" type="url" value={form.photo_url} onChange={event => setForm(value => ({ ...value, photo_url: event.target.value }))} placeholder="https://…"/><small>Use a controlled Sousa Murray-hosted head-and-shoulders photograph. If omitted, the credential and public verifier will clearly state that no photograph is on file.</small></label>
        </div>
        <div className="cid-step-actions"><span/><button className="cid-button cid-button-primary" disabled={!canNext1} onClick={() => setIssueStep(2)}>Continue <ChevronRight size={15}/></button></div>
      </div>}

      {issueStep === 2 && <div className="cid-issue-stage">
        <div className="cid-stage-intro"><div className="cid-stage-icon"><Building2 size={22}/></div><div><h4>Verify the company relationship</h4><p className="govuk-body">Load the legal entity from Companies House, then choose whether the person's authority comes from a Companies House appointment or the Sousa Murray staff record.</p></div></div>
        <div className="cid-form-grid">
          <div className="wide cid-company-search">
            <label className="govuk-label"><span>Companies House company number</span><input className="govuk-input" value={form.company_number} onChange={event => setForm(value => ({ ...value, company_number: event.target.value.toUpperCase() }))}/></label>
            <button className="cid-button cid-button-ghost" type="button" onClick={lookup} disabled={busy}><Search size={15}/>Find company</button>
          </div>
          {company && <div className="wide cid-company-match"><div className="cid-match-icon"><CheckCircle2 size={20}/></div><div><span>OFFICIAL COMPANY RECORD</span><strong>{company.company_name}</strong><small>{company.company_number} · {titleCase(company.company_status)} · {titleCase(company.company_type)}</small></div></div>}
          <div className="wide cid-authority-choice">
            <button type="button" className={form.relationship_source === 'companies_house' ? 'active' : ''} onClick={() => setForm(value => ({ ...value, relationship_source: 'companies_house', companies_house_officer_key: value.companies_house_officer_key }))}>
              <BadgeCheck size={18}/><span><strong>Companies House officer</strong><small>Director, secretary or another recorded officer appointment.</small></span>
            </button>
            <button type="button" className={form.relationship_source === 'employer' ? 'active' : ''} onClick={() => setForm(value => ({ ...value, relationship_source: 'employer', companies_house_officer_key: '' }))}>
              <Users size={18}/><span><strong>Sousa Murray staff member</strong><small>Employer-issued ID backed by the person's current staff record.</small></span>
            </button>
          </div>
          {form.relationship_source === 'companies_house' && <label className="wide govuk-label"><span>Companies House appointment</span><select className="govuk-select" disabled={!officers.length} value={form.companies_house_officer_key} onChange={event => setForm(value => ({ ...value, companies_house_officer_key: event.target.value }))}><option value="">{officers.length ? 'Select current officer appointment' : 'Load the company first'}</option>{officers.map(row => <option key={row.officer_key} value={row.officer_key} disabled={Boolean(row.resigned_on)}>{row.name} · {titleCase(row.officer_role)}{row.resigned_on ? ` · ended ${row.resigned_on}` : ''}</option>)}</select></label>}
          {form.relationship_source === 'employer' && <div className="wide cid-employer-assurance"><ShieldCheck size={18}/><div><strong>Employer relationship</strong><small>The credential will remain valid only while the holder's Sousa Murray staff record and the company remain active.</small></div></div>}
        </div>
        <div className="cid-step-actions"><button className="cid-button cid-button-ghost" onClick={() => setIssueStep(1)}>Back</button><button className="cid-button cid-button-primary" disabled={!canNext2} onClick={() => setIssueStep(3)}>Continue <ChevronRight size={15}/></button></div>
      </div>}

      {issueStep === 3 && <div className="cid-issue-stage">
        <div className="cid-stage-intro"><div className="cid-stage-icon"><ShieldCheck size={22}/></div><div><h4>Review and formally issue the credential</h4><p className="govuk-body">Confirm the credential identity, issuer, authority source, photograph status and validity before issue.</p></div></div>
        {!data?.security?.personal_code_encryption_configured && <div className="cid-security-warning"><LockKeyhole size={19}/><div><strong>Personal-code vault is not configured</strong><p className="govuk-body">You can issue a Company ID without storing a Companies House personal code. Configure <code>COMPANY_ID_ENCRYPTION_KEY</code> in Secrets & Variables before entering one.</p></div></div>}
        {!form.photo_url && <div className="cid-photo-assurance-warning"><UserRound size={19}/><div><strong>No holder photograph is on file</strong><p className="govuk-body">The credential can still be issued, but a formal Company ID is stronger when a controlled holder photograph is present. The verifier will disclose that no photograph is stored.</p></div></div>}
        <div className="cid-form-grid">
          <label className="govuk-label"><span>Companies House personal code <em>optional</em></span><input className="govuk-input" autoComplete="off" disabled={!data?.security?.personal_code_encryption_configured} value={form.companies_house_personal_code} onChange={event => setForm(value => ({ ...value, companies_house_personal_code: event.target.value.toUpperCase() }))} placeholder="11 characters" maxLength={16}/><small>Stored encrypted. It is never included in public verification.</small></label>
          <label className="govuk-label"><span>Credential expiry <em>optional</em></span><input className="govuk-input" type="date" value={form.expires_at} onChange={event => setForm(value => ({ ...value, expires_at: event.target.value }))}/><small>Leave blank for no fixed expiry. The credential can still be suspended or revoked.</small></label>
          <label className="wide govuk-label"><span>Holder identity check</span><select className="govuk-select" value={form.identity_check_method} onChange={event => setForm(value => ({ ...value, identity_check_method: event.target.value }))}><option value="staff_record">Existing verified staff record</option><option value="in_person">In-person visual check</option><option value="remote_video">Remote video check</option><option value="other_company_process">Other approved company process</option></select><small>Record only the assurance method. Do not enter passport, driving licence or other document numbers here.</small></label>
          <label className="wide cid-identity-confirmation govuk-label"><input type="checkbox" checked={form.identity_check_confirmed} onChange={event => setForm(value => ({ ...value, identity_check_confirmed: event.target.checked }))}/><span><strong>I confirm the holder details have been checked</strong><small>I have checked the holder against the selected staff record and the company information above. If a photograph is supplied, it belongs to this holder.</small></span></label>
        </div>

        <div className="cid-review-card">
          <div className="cid-review-title"><BadgeCheck size={20}/><div><span>READY TO ISSUE</span><strong>Credential review</strong></div></div>
          <dl>
            <div><dt>Credential standard</dt><dd>SMCID-1</dd></div><div><dt>Credential type</dt><dd>{form.relationship_source === 'employer' ? 'Staff Identification' : 'Corporate Officer Identification'}</dd></div><div><dt>Holder</dt><dd>{form.legal_name || selectedStaff?.display_name || '—'}</dd></div>
            <div><dt>Title</dt><dd>{form.internal_title || selectedStaff?.job_title || '—'}</dd></div>
            <div><dt>Company</dt><dd>{company?.company_name || '—'}</dd></div>
            <div><dt>Company number</dt><dd>{company?.company_number || form.company_number || '—'}</dd></div>
            <div><dt>Staff ID</dt><dd>{selectedStaff?.staff_id_number || `${data?.defaults?.staff_id_prefix || 'SMG'}-###### · assigned on issue`}</dd></div><div><dt>Authority source</dt><dd>{form.relationship_source === 'employer' ? 'Sousa Murray employer record' : 'Companies House'}</dd></div><div><dt>Company role</dt><dd>{form.relationship_source === 'employer' ? (form.internal_title || selectedStaff?.job_title || 'Staff member') : (titleCase(selectedOfficer?.officer_role) || '—')}</dd></div>
            <div><dt>Holder photograph</dt><dd>{form.photo_url ? 'On file' : 'Not on file'}</dd></div><div><dt>Identity assurance</dt><dd>{form.identity_check_confirmed ? titleCase(form.identity_check_method) : 'Not confirmed'}</dd></div><div><dt>Personal code</dt><dd>{form.companies_house_personal_code ? 'Will be stored encrypted' : 'Not stored'}</dd></div>
          </dl>
        </div>

        <div className="cid-step-actions"><button className="cid-button cid-button-ghost" onClick={() => setIssueStep(2)}>Back</button><button className="cid-button cid-button-primary" onClick={issue} disabled={busy || !canIssue}>{busy ? 'Issuing…' : 'Issue Company ID'} <BadgeCheck size={15}/></button></div>
      </div>}
    </section>

    <aside className="cid-panel cid-issue-aside">
      <span className="cid-panel-kicker">WHAT GETS ISSUED</span>
      <h3 className="govuk-heading-s">Formal company-issued identification</h3>
      <p className="cid-issuer-disclaimer govuk-body">Employer-issued identification, not government ID. This credential does not replace a passport, driving licence or other government identity document.</p>
      <div className="cid-preview-card">
        <div className="cid-preview-top"><Fingerprint size={18}/><span>SOUSA MURRAY · COMPANY ID</span><BadgeCheck size={20}/></div>
        <div className="cid-preview-avatar">{form.photo_url ? <img src={form.photo_url} alt=""/> : initials(form.legal_name || selectedStaff?.display_name)}</div>
        <strong>{form.legal_name || selectedStaff?.display_name || 'Credential holder'}</strong>
        <span>{form.internal_title || selectedStaff?.job_title || 'Company role'}</span>
        <div className="cid-preview-company">{company?.company_name || 'Issuing legal entity'}</div><div className="cid-preview-staff-id">Staff ID · {selectedStaff?.staff_id_number || `${data?.defaults?.staff_id_prefix || 'SMG'}-######`} · SMCID-1</div>
      </div>
      <ul className="cid-feature-list">
        <li><CheckCircle2 size={16}/><span>Permanent Sousa Murray Staff ID number</span></li><li><CheckCircle2 size={16}/><span>Live company authority relationship</span></li>
        <li><CheckCircle2 size={16}/><span>Rotating QR verification</span></li>
        <li><CheckCircle2 size={16}/><span>Central suspend and revoke controls</span></li>
        <li><CheckCircle2 size={16}/><span>Formal credential serial and SMCID-1 standard</span></li><li><CheckCircle2 size={16}/><span>Auditable issuance and verification events</span></li>
        <li><CreditCard size={16}/><span>Wallet-ready architecture</span></li>
      </ul>
    </aside>
  </div>;
}

function Step({ number, label, active, complete }) {
  return <div className={`cid-step ${active ? 'active' : ''} ${complete ? 'complete' : ''}`}><span>{complete ? <CheckCircle2 size={16}/> : number}</span><strong>{label}</strong></div>;
}

function VerificationHub({ selected, qr, presentation, setSelectedId, credentials }) {
  return <div className="cid-verification-grid">
    <section className="cid-panel">
      <div className="cid-panel-heading">
        <div><span className="cid-panel-kicker">LIVE VERIFICATION</span><h3 className="govuk-heading-s">Verify a Company ID</h3><p className="govuk-body">Every active Company ID can present a rotating QR code that resolves to its current credential status.</p></div>
      </div>

      <div className="cid-verification-explainer">
        <div><span>1</span><ScanLine size={23}/><strong>Show</strong><p className="govuk-body">The holder opens their Company ID and displays a rotating QR.</p></div>
        <ChevronRight size={20}/>
        <div><span>2</span><Smartphone size={23}/><strong>Scan</strong><p className="govuk-body">Any authorised verifier scans the code on a phone or device.</p></div>
        <ChevronRight size={20}/>
        <div><span>3</span><BadgeCheck size={23}/><strong>Check</strong><p className="govuk-body">The verifier sees the live Staff ID, credential status and current company-authority relationship.</p></div>
      </div>

      <div className="cid-privacy-banner"><ShieldCheck size={20}/><div><strong>Selective, privacy-conscious verification</strong><p className="govuk-body">The verifier receives the credential facts needed to establish company authority. The Companies House personal code is never included.</p></div></div>
    </section>

    <section className="cid-panel cid-verifier-demo">
      <div className="cid-panel-heading compact"><div><span className="cid-panel-kicker">PRESENTATION</span><h3 className="govuk-heading-s">Live QR</h3></div></div>
      {selected ? <>
        <label className="cid-select-label govuk-label"><span>Credential</span><select className="govuk-select" value={selected.id} onChange={event => setSelectedId(event.target.value)}>{credentials.map(row => <option key={row.id} value={row.id}>{row.legal_name} · {row.staff_id_number || row.credential_number}</option>)}</select></label>
        <div className="cid-verifier-qr">{qr ? <img src={qr} alt="Live Company ID QR"/> : <div className="cid-qr-skeleton large"><ScanLine size={46}/></div>}</div>
        <strong className="cid-verifier-name">{selected.legal_name}</strong>
        <span className="cid-verifier-company">{selected.company_name}</span>
        <div className="cid-live-chip"><span/> LIVE · rotates automatically</div>
        <small>{presentation?.expires_at ? `Current presentation expires ${dateTime(presentation.expires_at)}` : 'A secure presentation is being prepared.'}</small>
        {presentation?.verification_url && <a className="cid-button cid-button-primary cid-full-button" href={presentation.verification_url} target="_blank" rel="noreferrer">Open verifier <ExternalLink size={14}/></a>}
      </> : <EmptyState title="No credential available" body="Issue a Company ID before testing the live verifier."/>}
    </section>
  </div>;
}

function WalletSecurity({ data, busy, onTest, testResult }) {
  const vaultReady = Boolean(data?.security?.personal_code_encryption_configured);
  const passStudioReady = Boolean(data?.wallet?.apple?.configured);
  return <div className="cid-wallet-layout">
    <section className="cid-panel">
      <div className="cid-panel-heading">
        <div><span className="cid-panel-kicker">DIGITAL WALLET</span><h3 className="govuk-heading-s">Apple Wallet via Pass Studio</h3><p className="govuk-body">Customer Operations remains the credential authority. Pass Studio signs and delivers the Apple Wallet pass while live Sousa Murray verification remains authoritative.</p></div>
        {passStudioReady && <button className="cid-button cid-button-ghost cid-button-small" onClick={onTest} disabled={busy}><RefreshCw size={14}/>Test connection</button>}
      </div>
      <div className="cid-wallet-cards">
        <WalletCard title="Apple Wallet" configured={passStudioReady} body={passStudioReady ? 'Pass Studio is configured. Active Company IDs can be issued as signed Apple Wallet passes.' : 'Add the PASS_STUDIO_API_KEY secret and PASS_STUDIO_COMPANY_ID_PASS_ID configuration before issuing passes.'}/>
        <WalletCard title="Google Wallet" configured={false} body="Google Wallet remains disabled for Company ID. This integration currently issues Apple Wallet credentials only."/>
      </div>
      {testResult?.ok && <div className="cid-notice">Pass Studio connection verified{testResult?.pass?.name ? ` · ${testResult.pass.name}` : ''}{testResult?.account?.credits !== null && testResult?.account?.credits !== undefined ? ` · ${testResult.account.credits} credits` : ''}.</div>}
      <p className="cid-wallet-note govuk-body"><LockKeyhole size={15}/> The Pass Studio API key stays server-side in Cloudflare. Staff receive only the signed Apple Wallet delivery URL.</p>
    </section>

    <section className="cid-panel">
      <div className="cid-panel-heading"><div><span className="cid-panel-kicker">SECURITY MODEL</span><h3 className="govuk-heading-s">Built to minimise exposed data</h3></div></div>
      <div className="cid-security-grid">
        <SecurityFeature icon={KeyRound} title="Encrypted restricted secret" body="Companies House personal codes are stored separately and encrypted with AES-GCM." state={vaultReady ? 'Configured' : 'Needs configuration'} good={vaultReady}/>
        <SecurityFeature icon={ScanLine} title="Short-lived verifier" body="The displayed QR uses a random 32-byte token with a 90-second lifetime." state="Active" good/>
        <SecurityFeature icon={Building2} title="Source relationship" body="Company status and the holder's authority relationship are re-checked, including Companies House appointments where applicable." state="Connected" good/>
        <SecurityFeature icon={Activity} title="Audit trail" body="Issuance, verification presentations, secret reveals and status changes create events." state="Active" good/>
      </div>
    </section>

    <section className="cid-panel">
      <div className="cid-panel-heading"><div><span className="cid-panel-kicker">PLATFORM PRINCIPLES</span><h3 className="govuk-heading-s">What Company ID proves</h3></div></div>
      <div className="cid-principles">
        <div><Fingerprint size={20}/><div><strong>Person</strong><p className="govuk-body">Who the credential was issued to.</p></div></div>
        <div><Building2 size={20}/><div><strong>Organisation</strong><p className="govuk-body">The legal company the credential relates to.</p></div></div>
        <div><BadgeCheck size={20}/><div><strong>Authority</strong><p className="govuk-body">The person's current employer or Companies House authority relationship.</p></div></div>
        <div><ShieldCheck size={20}/><div><strong>Status</strong><p className="govuk-body">Whether Sousa Murray currently considers the credential active and valid.</p></div></div>
      </div>
    </section>
  </div>;
}

function WalletCard({ title, configured, body }) {
  return <div className="cid-wallet-card"><div className="cid-wallet-icon"><Smartphone size={24}/></div><div><span className={`cid-wallet-status ${configured ? 'ready' : ''}`}>{configured ? 'READY' : 'SETUP REQUIRED'}</span><h4>{title}</h4><p className="govuk-body">{body}</p></div></div>;
}

function SecurityFeature({ icon: Icon, title, body, state, good }) {
  return <div className="cid-security-feature"><div className="cid-security-icon"><Icon size={20}/></div><div><strong>{title}</strong><p className="govuk-body">{body}</p><span className={good ? 'good' : 'warn'}>{good ? <CheckCircle2 size={14}/> : <XCircle size={14}/>} {state}</span></div></div>;
}
